import { useEffect, useRef, useState } from 'react';

import {
  PayPalScriptProvider,
  BraintreePayPalButtons,
  usePayPalScriptReducer,
  OnApproveBraintreeData,
  OnApproveBraintreeActions,
  CreateOrderBraintreeActions,
} from '@paypal/react-paypal-js';
import Box from '@mui/material/Box/Box';
import { ErrorBoundary } from 'react-error-boundary';

import { sendFEEvent } from '../../eventUtil';
import { ESdkEvents, MixpanelEvents } from '../../contracts';
import { logAlways } from '../../utils/coralogix-rum';
import { useCheckout } from '../../hooks/use-checkout';
import { PaypalButtonLoader } from './PaypalButtonLoader';
import { usePaypalSubmit } from './hooks/use-paypal-submit';
import { sendMixpanelEvent } from '../../utils/mixpanel-utils';
import { getPaypalAmount, PaypalPaymentProvider } from './utils';
import { usePaymentState } from '../Checkout/hooks/use-payment-state';
import { EPaymentProvider, EBEPaymentMethodsName } from '../../contracts';
import { useCheckoutFlowType } from '../Checkout/hooks/use-checkout-flow-type';
import { apmLatencyTracker } from '../../utils/apm-latency-tracker';
import { getEnvVariable } from '../../utils/get-env-variable';

interface PaypalButtonProps {
  onReady: (isAvailable: boolean) => void;
}

export function PaypalButton({ onReady }: PaypalButtonProps) {
  const { checkoutData } = useCheckout();
  const isOnReadyCalled = useRef(false);

  const paypalPM = checkoutData?.paymentMethods?.find(
    (pm) => pm.paymentMethodName === EBEPaymentMethodsName.paypal,
  );
  const paypalPk =
    paypalPM?.provider === EPaymentProvider.braintree
      ? checkoutData?.pks.braintree
      : checkoutData?.pks.paypal;

  if (!paypalPk) {
    if (!isOnReadyCalled.current) {
      onReady(false);
      isOnReadyCalled.current = true;
    }
    return null;
  }

  const handlePayPalFatalError = (error: Error) => {
    logAlways('Paypal button fatal error:', { error });
    onReady?.(false);
  };

  const { pricing } = checkoutData!;
  const currencyCode = pricing?.currencyCode.toUpperCase();

  return (
    <ErrorBoundary FallbackComponent={() => null} onError={handlePayPalFatalError}>
      <PayPalScriptProvider
        options={{
          clientId:
            getEnvVariable('REACT_APP_PAYPAL_CLIENT_ID', process.env.REACT_APP_PAYPAL_CLIENT_ID) ||
            'test',
          dataClientToken: paypalPk,
          disableFunding:
            'card,venmo,credit,paylater,bancontact,blik,eps,giropay,ideal,mybank,p24,sepa,sofort,mercadopago',
          enableFunding: 'paypal',
          currency: currencyCode,
        }}
      >
        <PaypalButtonWrapper onReady={onReady} />
      </PayPalScriptProvider>
    </ErrorBoundary>
  );
}

function PaypalButtonWrapper({ onReady }: { onReady: (isAvailable: boolean) => void }) {
  const [{ isRejected }] = usePayPalScriptReducer();
  const { handlePaypalSubmit } = usePaypalSubmit();
  const [isPaypalButtonReady, setIsPaypalButtonReady] = useState(false);
  const { checkoutData, getEventParams, enrichEventParams } = useCheckout();
  const { offer, pricing } = checkoutData!;
  const { isPaymentLinksFlow } = useCheckoutFlowType();

  const priceBreakdown = usePaymentState((state) => state.priceBreakdown);

  const paypalPM = checkoutData?.paymentMethods?.find(
    (pm) => pm.paymentMethodName === EBEPaymentMethodsName.paypal,
  );
  const paypalPk =
    paypalPM?.provider === EPaymentProvider.braintree
      ? checkoutData?.pks.braintree
      : checkoutData?.pks.paypal;

  const hasMissingPricingParameter =
    priceBreakdown?.priceNet === undefined ||
    priceBreakdown?.priceTotal === undefined ||
    priceBreakdown?.tax === undefined;

  useEffect(() => {
    if (isRejected) {
      setIsPaypalButtonReady(false);
      onReady(false);
      logAlways('Paypal button rejected', {
        isRejected,
        checkoutData: JSON.stringify(checkoutData),
      });
    }
  }, [isRejected]);

  useEffect(() => {
    if (isPaymentLinksFlow) {
      onReady(true);
    }
  }, [isPaymentLinksFlow]);

  useEffect(() => {
    try {
      apmLatencyTracker.startTracking('Paypal', paypalPM?.id);
    } catch (error) {
      console.error('Failed to start APM latency tracking:', error);
    }
  }, [paypalPM?.id]);

  if (hasMissingPricingParameter || !paypalPk) {
    onReady(false);
    return null;
  }

  if (isRejected) {
    return null;
  }

  const currencyCode = pricing?.currencyCode.toUpperCase();

  const multiplier = pricing?.multiplier;

  const onCreateOrder = async (
    data: Record<string, unknown>,
    actions: CreateOrderBraintreeActions,
  ) => {
    const priceBreakdown = usePaymentState.getState().priceBreakdown!;
    const amount = getPaypalAmount(priceBreakdown?.priceTotal, multiplier);
    const unitAmount = getPaypalAmount(priceBreakdown?.priceNet, multiplier);
    const unitTaxAmount = getPaypalAmount(priceBreakdown?.tax, multiplier);
    return actions.braintree.createPayment({
      flow: 'checkout',
      amount,
      currency: currencyCode,
      requestBillingAgreement: false,
      intent: 'capture',
      enableShippingAddress: true,
      shippingAddressEditable: false,
      billingAgreementDescription: 'Description of the billng agreement to display to the customer',
      lineItems: [
        {
          quantity: '1',
          unitAmount,
          name: offer.name || '',
          kind: 'debit',
          unitTaxAmount,
          url: undefined,
          description: '',
          productCode: offer.sku,
        },
      ],
    });
  };

  const onApprove = (data: OnApproveBraintreeData, actions: OnApproveBraintreeActions) => {
    return actions.braintree.tokenizePayment(data).then(async (payload) => {
      handlePaypalSubmit({
        paymentNonce: payload.nonce,
        email: payload.details.email,
        paymentMethodId: paypalPM?.id!,
        paymentMethodDetails: payload.details,
        paymentProvider: paypalPM?.provider as PaypalPaymentProvider,
      });
    });
  };
  return (
    <PayPalScriptProvider
      options={{
        clientId:
          getEnvVariable('REACT_APP_PAYPAL_CLIENT_ID', process.env.REACT_APP_PAYPAL_CLIENT_ID) ||
          'test',
        dataClientToken: paypalPk,
        disableFunding:
          'card,venmo,credit,paylater,bancontact,blik,eps,giropay,ideal,mybank,p24,sepa,sofort,mercadopago',
        enableFunding: 'paypal',
        currency: currencyCode,
      }}
    >
      <Box position="relative" width="100%" height={isPaymentLinksFlow ? 44 : 'auto'}>
        {!isPaypalButtonReady && isPaymentLinksFlow && (
          <Box position="absolute" width="100%" height="100%" zIndex={1}>
            <PaypalButtonLoader />
          </Box>
        )}
        <Box position="relative" width="100%" height="100%" zIndex={2}>
          <BraintreePayPalButtons
            onClick={(_, actions) => {
              sendFEEvent(ESdkEvents.ON_PAYMENT_INTENT, {
                ...getEventParams(),
                paymentMethodName: paypalPM?.paymentMethodDisplayName,
              });
              sendMixpanelEvent(MixpanelEvents.ON_PAY_BUTTON_CLICKED, {
                ...enrichEventParams(),
                paymentMethodName: paypalPM?.paymentMethodDisplayName,
              });
              sendMixpanelEvent(MixpanelEvents.PAYMENT_CHOOSE, {
                ...enrichEventParams(),
                paymentMethodName: EBEPaymentMethodsName.paypal,
              });
              return actions.resolve();
            }}
            onInit={() => {
              setIsPaypalButtonReady(true);
              onReady?.(true);

              try {
                apmLatencyTracker.endTracking('Paypal', paypalPM?.id, {
                  paymentMethodName: paypalPM?.paymentMethodName,
                  paymentMethodDisplayName: paypalPM?.paymentMethodDisplayName,
                  provider: paypalPM?.provider,
                });
              } catch (error) {
                console.error('Failed to end APM latency tracking:', error);
              }
            }}
            style={{ height: 44 }}
            className="button"
            createOrder={onCreateOrder}
            onApprove={onApprove}
            onError={(err) => console.error(err)}
          />
        </Box>
      </Box>
    </PayPalScriptProvider>
  );
}
